<template>
  <div class="notification-container">
    <button @click="toggleNotificationBox" class="notification-btn">
      <div class="neo-icon">
        <i class="fas fa-bell"></i>
        <div class="glow"></div>
      </div>
      <span v-if="count > 0" class="notification-badge">{{ formatCount(count) }}</span>
    </button>
    
    <transition name="cyber-dropdown">
      <div v-if="showNotifications" class="notification-box">
        <div class="notification-header">
          <div class="header-left">
            <i class="fas fa-bell"></i>
            <h3>NOTIFICAÇÕES</h3>
          </div>
          <button @click="refreshNotifications" class="refresh-btn" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'rotating': loading }"></i>
          </button>
        </div>
        
        <div class="notification-content">
          <div v-if="loading" class="loading-container">
            <div class="cyber-loader">
              <div class="loader-ring"></div>
              <div class="loader-ring"></div>
              <div class="loader-ring"></div>
            </div>
            <span>BUSCANDO...</span>
          </div>
          
          <template v-else-if="notifications.length > 0">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="notification-item"
              @click="handleNotificationClick(notification)"
            >
              <div class="item-border"></div>
              <div class="notification-content-inner">
                <p>{{ notification.text }}</p>
                <span class="notification-time">{{ formatDate(notification.createdAt) }}</span>
              </div>
            </div>
          </template>
          
          <div v-else class="empty-notifications">
            <div class="neo-icon large">
              <i class="fas fa-inbox"></i>
              <div class="glow"></div>
            </div>
            <p>NENHUMA NOTIFICAÇÃO</p>
          </div>
        </div>
        
        <div class="notification-footer">
          <button @click="viewAllNotifications" class="view-all-btn">
            VER TODAS
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'NotificationBox',
  data() {
    return {
      showNotifications: false
    }
  },
  computed: {
    ...mapGetters('notification', {
      notifications: 'getNotifications',
      count: 'getCount',
      loading: 'isLoading'
    })
  },
  mounted() {
    // Initialize when player data is loaded (check every second)
    const initInterval = setInterval(() => {
      if (this.$store.getters['player/player']) {
        this.getNotificationCount()
        this.startPolling()
        clearInterval(initInterval)
      }
    }, 1000)
    
    // Add click outside listener to close the dropdown
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    // Stop polling when component is destroyed
    this.stopPolling()
    
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    ...mapActions('notification', [
      'fetchRecentNotifications',
      'deleteNotification',
      'getNotificationCount',
      'startPolling',
      'stopPolling'
    ]),
    ...mapActions('SoundController', ['playSound']),
    
    toggleNotificationBox(event) {
      // Prevent event from bubbling to document click handler
      event.stopPropagation()
      
      this.showNotifications = !this.showNotifications
      
      // Play UI sound when clicking notification button
      this.playSound({ category: 'ui', type: 'click' })
      
      // If opening the box, fetch latest notifications
      if (this.showNotifications) {
        this.refreshNotifications()
      }
    },
    
    async refreshNotifications() {
      await this.fetchRecentNotifications()
      await this.getNotificationCount()
    },
    
    async handleNotificationClick(notification) {
      // Delete the notification first
      await this.deleteNotification(notification.id)
      
      // Navigate to the link
      if (notification.link) {
        // Check if it's an external link
        if (notification.link.startsWith('http://') || notification.link.startsWith('https://')) {
          window.open(notification.link, '_blank')
        } else {
          // Internal link within the app
          this.$router.push(notification.link)
        }
      }
      
      // Close the notification box
      this.showNotifications = false
    },
    
    viewAllNotifications() {
      this.$router.push('/notifications')
      this.showNotifications = false
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      
      // If it's today, just show the time
      const today = new Date()
      if (date.toDateString() === today.toDateString()) {
        return `HOJE • ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      }
      
      // If it's yesterday
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return `ONTEM • ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      }
      
      // Otherwise, show the full date
      return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()} • ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    },
    
    formatCount(count) {
      if (count > 99) return '99+'
      return count
    },
    
    handleClickOutside(event) {
      const container = this.$el
      if (container && !container.contains(event.target) && this.showNotifications) {
        this.showNotifications = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Cyberpunk color palette
$neon-blue: #c41e3a; // Já está em crimson red
$neon-pink: #ff003c;
$neon-yellow: #ffbb00;
$dark-bg: #0a0a0f;
$darker-bg: #050508;
$cyber-gray: rgba(180, 180, 190, 0.15);
$text-color: #e0e0ff;

.notification-container {
  position: relative;
  display: inline-block;
  margin-left: 15px;
}

.notification-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  position: relative;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  @media (max-width: 768px) {
    padding: 0.4rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.3rem;
  }
  
  .neo-icon {
    position: relative;
    width: 2.4rem;
    height: 2.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: $darker-bg;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    
    @media (max-width: 480px) {
      width: 2.2rem;
      height: 2.2rem;
    }
    
    @media (max-width: 360px) {
      width: 2rem;
      height: 2rem;
    }
    
    i {
      color: $text-color;
      font-size: 1.2rem;
      position: relative;
      z-index: 2;
      text-shadow: 0 0 5px rgba(196, 30, 58, 0.5);
      
      @media (max-width: 360px) {
        font-size: 1rem;
      }
    }
    
    .glow {
      position: absolute;
      width: 100%;
      height: 2px;
      background: $neon-blue;
      bottom: 0;
      left: 0;
      filter: blur(3px);
      opacity: 0.7;
      transform: translateY(50%);
      z-index: 1;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.2), transparent); // Alterado de rgba(0, 240, 255, 0.2)
    }
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.6), transparent); // Alterado de rgba(0, 240, 255, 0.6)
    }
  }
  
  &:hover .neo-icon {
    box-shadow: 0 0 20px rgba(196, 30, 58, 0.3); // Alterado de rgba(0, 240, 255, 0.3)
    
    i {
      color: white;
      text-shadow: 0 0 8px rgba(196, 30, 58, 0.8); // Alterado de rgba(0, 240, 255, 0.8)
    }
    
    .glow {
      opacity: 1;
      height: 3px;
      filter: blur(5px);
    }
  }
  
  .notification-badge {
    position: absolute;
    top: -3px;
    right: -3px;
    background: $neon-pink;
    color: white;
    border-radius: 50%;
    padding: 0.1rem 0.4rem;
    font-size: 0.7rem;
    font-weight: 600;
    min-width: 1.2rem;
    min-height: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 10px rgba(255, 0, 60, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.2);
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    font-family: 'Rajdhani', sans-serif;
    letter-spacing: 0.5px;
    
    @media (max-width: 360px) {
      min-width: 1rem;
      min-height: 1rem;
      font-size: 0.6rem;
      padding: 0.1rem 0.3rem;
      top: -2px;
      right: -2px;
    }
    
    &::before {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 1px solid rgba(255, 0, 60, 0.3);
      animation: pulse 2s infinite;
    }
  }
}

.notification-box {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 350px;
  max-height: 450px;
  background: $dark-bg;
  border-radius: 3px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.8);
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(196, 30, 58, 0.5);
  
  @media (max-width: 480px) {
    width: 300px;
    max-height: 400px;
    right: -10px;
  }
  
  @media (max-width: 360px) {
    width: 280px;
    right: -20px;
  }
  
  // For very small screens, make the notification box take full width
  @media (max-width: 320px) {
    width: calc(100vw - 40px);
    right: -25px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, $neon-blue, transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -8px;
    right: 28px;
    width: 15px;
    height: 15px;
    background: $dark-bg;
    transform: rotate(45deg);
    border-top: 1px solid rgba(196, 30, 58, 0.2);
    border-left: 1px solid rgba(196, 30, 58, 0.2);
    
    @media (max-width: 480px) {
      right: 18px;
    }
    
    @media (max-width: 360px) {
      right: 28px;
    }
  }
}

.notification-header {
  padding: 1rem 1.2rem;
  border-bottom: 1px solid rgba(196, 30, 58, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(196, 30, 58, 0.05);
  
  @media (max-width: 360px) {
    padding: 0.8rem 1rem;
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    
    @media (max-width: 360px) {
      gap: 0.6rem;
    }
    
    i {
      color: $neon-blue;
      font-size: 1.1rem;
      
      @media (max-width: 360px) {
        font-size: 1rem;
      }
    }
    
    h3 {
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
      color: white;
      letter-spacing: 1px;
      font-family: 'Rajdhani', sans-serif;
      
      @media (max-width: 360px) {
        font-size: 0.9rem;
        letter-spacing: 0.5px;
      }
    }
  }
  
  .refresh-btn {
    background: none;
    border: none;
    color: $text-color;
    cursor: pointer;
    font-size: 0.9rem;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    transition: all 0.2s;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(196, 30, 58, 0.1); // Alterado de rgba(0, 240, 255, 0.1)
      opacity: 0;
      transition: opacity 0.2s;
    }
    
    &:hover {
      color: white;
      
      &::before {
        opacity: 1;
      }
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .rotating {
      animation: spin 1s linear infinite;
    }
  }
}

.notification-content {
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  
  @media (max-width: 480px) {
    max-height: 250px;
  }
  
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: $darker-bg;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(196, 30, 58, 0.3);
    border-radius: 3px;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 0;
    color: $text-color;
    
    .cyber-loader {
      position: relative;
      width: 50px;
      height: 50px;
      margin-bottom: 1rem;
      
      .loader-ring {
        position: absolute;
        inset: 0;
        border: 2px solid transparent;
        border-top-color: $neon-blue;
        border-radius: 50%;
        
        &:nth-child(1) {
          animation: spin 1s linear infinite;
        }
        
        &:nth-child(2) {
          border-top-color: $neon-pink;
          animation: spin 1.3s linear infinite reverse;
          scale: 0.8;
        }
        
        &:nth-child(3) {
          border-top-color: $neon-yellow;
          animation: spin 1.7s linear infinite;
          scale: 0.6;
        }
      }
    }
    
    span {
      font-size: 0.9rem;
      letter-spacing: 2px;
      font-family: 'Rajdhani', sans-serif;
      color: $text-color;
      opacity: 0.7;
    }
  }
  
  .notification-item {
    padding: 0;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    
    &:hover {
      background-color: rgba(196, 30, 58, 0.05);
      
      .item-border {
        opacity: 1;
      }
    }
    
    .item-border {
      position: absolute;
      inset: 0;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        left: 0;
        background: $neon-blue;
      }
    }
    
    .notification-content-inner {
      padding: 1rem 1.2rem;
      
      @media (max-width: 360px) {
        padding: 0.8rem 1rem;
      }
    }
    
    p {
      margin: 0 0 0.5rem 0;
      font-size: 0.95rem;
      color: white;
      line-height: 1.4;
      
      @media (max-width: 360px) {
        font-size: 0.85rem;
        line-height: 1.3;
        margin-bottom: 0.4rem;
      }
    }
    
    .notification-time {
      font-size: 0.75rem;
      color: rgba(224, 224, 255, 0.5);
      font-family: 'Rajdhani', sans-serif;
      letter-spacing: 1px;
      
      @media (max-width: 360px) {
        font-size: 0.7rem;
        letter-spacing: 0.5px;
      }
    }
  }
  
  .empty-notifications {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 0;
    color: $text-color;
    
    @media (max-width: 360px) {
      padding: 2rem 0;
    }
    
    .neo-icon.large {
      width: 60px;
      height: 60px;
      margin-bottom: 1rem;
      border-radius: 30px;
      
      @media (max-width: 360px) {
        width: 50px;
        height: 50px;
        border-radius: 25px;
      }
      
      i {
        font-size: 2rem;
        
        @media (max-width: 360px) {
          font-size: 1.6rem;
        }
      }
    }
    
    p {
      font-size: 0.9rem;
      letter-spacing: 1px;
      opacity: 0.7;
      font-family: 'Rajdhani', sans-serif;
      
      @media (max-width: 360px) {
        font-size: 0.8rem;
      }
    }
  }
}

.notification-footer {
  padding: 1rem;
  border-top: 1px solid rgba(196, 30, 58, 0.3);
  display: flex;
  justify-content: center;
  background: rgba(196, 30, 58, 0.03);
  
  @media (max-width: 360px) {
    padding: 0.8rem;
  }
  
  .view-all-btn {
    background: none;
    border: 1px solid rgba(196, 30, 58, 0.3);
    color: $neon-blue;
    cursor: pointer;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    transition: all 0.2s;
    padding: 0.5rem 1.2rem;
    border-radius: 2px;
    font-weight: 600;
    letter-spacing: 1px;
    position: relative;
    overflow: hidden;
    font-family: 'Rajdhani', sans-serif;
    
    @media (max-width: 360px) {
      font-size: 0.75rem;
      padding: 0.4rem 1rem;
      letter-spacing: 0.5px;
    }
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.1), transparent);
      transform: translateX(-100%);
      transition: transform 0.3s;
    }
    
    &:hover {
      background: rgba(196, 30, 58, 0.05);
      box-shadow: 0 0 15px rgba(196, 30, 58, 0.2);
      
      &::before {
        transform: translateX(100%);
      }
    }
    
    i {
      font-size: 0.7rem;
    }
  }
}

// Animation for dropdown
.cyber-dropdown-enter-active,
.cyber-dropdown-leave-active {
  transition: all 0.3s ease;
}

.cyber-dropdown-enter-from,
.cyber-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@keyframes pulse {
  0% {
    opacity: 0.7;
    transform: scale(1);
  }
  70% {
    opacity: 0;
    transform: scale(1.5);
  }
  100% {
    opacity: 0;
    transform: scale(1.5);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
